'use client';

/**
 * PapiProvider - Context provider for PAPI lifecycle management
 *
 * Responsibilities:
 * - Triggers background pre-initialization on mount
 * - Provides observable status to components
 * - Handles cleanup on unmount
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getPapiStatus, subscribeToPapiStatus } from '@/lib/papi-status.client';
import type { PapiStatus } from '@/types';

// Context for PAPI status
const PapiContext = createContext<PapiStatus>('idle');

/**
 * Hook to get current PAPI initialization status
 * @returns Current status: 'idle' | 'initializing' | 'ready' | 'error'
 */
export function usePapiStatus(): PapiStatus {
	return useContext(PapiContext);
}

interface PapiProviderProps {
	children: ReactNode;
}

/**
 * Provider component that manages PAPI lifecycle
 * Place near the root of your app (e.g., inside Providers.tsx)
 */
export function PapiProvider({ children }: PapiProviderProps) {
	const [status, setStatus] = useState<PapiStatus>(getPapiStatus);

	useEffect(() => {
		let mounted = true;

		// Subscribe to status changes
		const unsubscribe = subscribeToPapiStatus(setStatus);

		// Trigger background initialization (dynamic import keeps Smoldot WASM out of initial bundle)
		import('@/lib/papi.client').then(({ preloadPapiInBackground }) => {
			if (mounted) preloadPapiInBackground();
		});

		// Cleanup on unmount
		return () => {
			mounted = false;
			unsubscribe();
			import('@/lib/papi.client').then(({ destroyPapiClients }) => {
				destroyPapiClients();
			});
		};
	}, []);

	return <PapiContext.Provider value={status}>{children}</PapiContext.Provider>;
}
